import { GraduationCap, Clock, ChevronRight, MessageSquare, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getDifficultyColor } from '../utils';

interface CourseListItem {
  courseId: string;
  slug: string;
  title: string;
  description: string | null;
  difficultyLevel: string;
  estimatedDurationHours: number | null;
  tags: string[];
  status: string;
}

interface CourseBrowserProps {
  courses: CourseListItem[];
  loading: boolean;
  onSelectCourse: (course: CourseListItem) => void;
  onStartFreeform: () => void;
}

export function CourseBrowser({
  courses,
  loading,
  onSelectCourse,
  onStartFreeform,
}: CourseBrowserProps) {
  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <GraduationCap className="h-12 w-12 mx-auto mb-4 text-primary" />
        <h1 className="text-3xl font-bold mb-2">Learn Anything</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Explore curated courses or learn any topic with AI-powered tutoring. Our AI tutor adapts
          to your pace and learning style.
        </p>
      </div>

      {/* Freeform Learning Card */}
      <Card
        className="mb-8 cursor-pointer hover:shadow-md transition-shadow border-primary/20 hover:border-primary/50"
        onClick={onStartFreeform}
      >
        <CardContent className="p-6 flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">Learn Any Topic</h3>
            <p className="text-muted-foreground text-sm">
              Start a conversation about any subject. Our AI tutor will guide you through
              personalized lessons.
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </CardContent>
      </Card>

      {/* Course Grid */}
      <h2 className="text-xl font-semibold mb-4">Featured Courses</h2>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {courses.map((course) => (
            <Card
              key={course.courseId}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onSelectCourse(course)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-lg">{course.title}</CardTitle>
                  <Badge className={cn('text-xs', getDifficultyColor(course.difficultyLevel))}>
                    {course.difficultyLevel}
                  </Badge>
                </div>
                <CardDescription>{course.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{course.estimatedDurationHours}h</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {course.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
